<template>
    <div ref="widgetWrap">
        <portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex w-full justify-end overflow-hidden items-center">
                <RedesignBaseSelect
                    v-model="sortByModel"
                    :multiple="false"
                    :placeholder="$t('general.sortBy')"
                    :data="extensionsSortOptions"
                    size="small"
                    class="select-type"
                    label-key="label"
                    value-key="value"
                />
            </div>
        </portal>
        <portal :to="`widget-header__${data.WidgetID}-action`">
            <div class="cursor-pointer hidden lg:block">
                <RedesignBasePopover
                    trigger="click"
                    width="380"
                >
                    <template #reference>
                        <RedesignBaseIconButton
                            color="primary"
                            icon="SlidersHorizontal"
                        />
                    </template>

                    <div class="redesign-columns-management">
                        <div class="flex items-center gap-1 mb-2">
                            <RedesignRadioGroup v-model="columnsMode">
                                <RedesignRadioButton
                                    :label="REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC"
                                    :name="REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC"
                                >
                                    {{ $t('settings.extension.columnsMode.static') }}
                                </RedesignRadioButton>
                                <RedesignRadioButton
                                    :label="REAL_TIME_WIDGET_CARDS_COLUMN_MODE.AUTO"
                                    :name="REAL_TIME_WIDGET_CARDS_COLUMN_MODE.AUTO"
                                >
                                    {{ $t('settings.extension.columnsMode.auto') }}
                                </RedesignRadioButton>
                            </RedesignRadioGroup>
                        </div>

                        <template v-if="columnsMode === REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC">
                            <div class="redesign-columns-management-field-label-wrapper">
                                <p class="redesign-columns-management-field-label">
                                    {{ layoutColumns + ' ' }}{{ $t('columns.layout') }}
                                </p>
                            </div>
                            <div class="w-full flex p-2 justify-between main-bg-light">
                                <div
                                    v-for="index in 15"
                                    :key="index"
                                    :class="{'bg-primary-100': index <= layoutColumns}"
                                    class="square mx-margin--1"
                                    @click="updateGrid(index)"
                                />
                            </div>
                        </template>

                        <template v-else>
                            <div class="redesign-columns-management-field-label-wrapper">
                                <p class="redesign-columns-management-field-label">
                                    {{ $t('settings.extension.autoColumns.size') }}
                                </p>
                            </div>
                            <div class="p-2">
                                <RedesignBaseSelect
                                    v-model="columnsSize"
                                    :multiple="false"
                                    :placeholder="$t('settings.extension.autoColumns.size')"
                                    :data="columnSizeList"
                                    label-key="label"
                                    value-key="value"
                                    class="w-full"
                                />
                            </div>
                        </template>
                    </div>
                </RedesignBasePopover>
            </div>
        </portal>
        <div class="flex justify-center">
            <fade-transition>
                <div
                    class="w-full redesign-extension-cards-wrap overflow-x-auto"
                    :class="{ 'columns-auto': !isStaticColumnsMode }"
                >
                    <template v-if="sortedExtensionsDebounced.length">
                        <div
                            ref="parent"
                            class="redesign-extension-cards p-4 gap-4"
                            :style="renderColumns"
                        >
                            <RedesignExtensionCard
                                v-for="(extension) in sortedExtensionsDebounced"
                                :key="extension[extensionIdProp]"
                                :extension="extension"
                                :settings="widgetSettings"
                                :cards-cols="layoutColumnsBasedOnColumnsMode"
                                :style="cardWrapStyles"
                                class="redesign-extension-card-container"
                                @recently-logged-out-expired="markExpired(`${extension.userID}:${extension.number}`)"
                            />
                        </div>
                    </template>
                </div>
            </fade-transition>
        </div>
    </div>
</template>
<script setup lang="ts">
import { EXTENSION_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import { get, partition } from 'lodash'
import { computed, defineEmits, ref, watch } from 'vue'
import { useThrottle, useElementBounding } from '@vueuse/core'
import times from 'lodash/times'
import i18n from '@/i18n'
import RedesignExtensionCard from '@/components/RedesignComponents/Cards/RedesignExtensionCard.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'
import {
    REAL_TIME_WIDGET_CARDS_COLUMN_MODE,
    REAL_TIME_WIDGET_CARDS_COLUMN_SIZE,
    realTimeSettings
} from '@/enum/defaultWidgetSettings'
import { getInitialExtensionTime, getInitialTime } from '@/util/timeUtils'
import cloneDeep from 'lodash/cloneDeep'
import { useAutoAnimate } from '@formkit/auto-animate/vue'
import orderBy from 'lodash/orderBy'
import { pageWidth } from '@/composables/useBreakpoints'
import { Statuses } from '@/enum/redesignStatusTypes'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import {
    isRecentlyLoggedOutEnabled,
    keepAfterExpiredKeys,
    shouldSkipExpiredLogout,
    useExpiredRecentlyLoggedOutKeys
} from '@/composables/useExpiredRecentlyLoggedOutKeys'
import { useExtensions } from '@/composables/useExtensions'
import { WidgetExtended } from '@/types/dashboard'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { Extension } from '@/types/extension'
import { getExtensionsSortOptions } from '@/enum/extensionsOptions'
import { currentAccountEntitiesExtensions } from '@/composables/globalData/useEntitiesData'

interface ICardSize {
    name: string,
    width: number,
    columns: number
}

type UserData = Record<string, string | number>

type UsersData = UserData[]

interface SortedExtension extends Extension {
    callDuration?: number
    statusTime?: number
}

const cardWidth = 600
const extensionIdProp = 'number'
const BASE_CARD_HEIGHT = 230

/*Props*/
const props = defineProps<{
    data: WidgetExtended
}>()

const emit = defineEmits([ 'on-update' ])

/* Data */
const widgetWrap = ref()
const [ parent ] = useAutoAnimate({
    duration: 300
})
const { width: widgetWidth, height: widgetHeight } = useElementBounding(widgetWrap)
const {
    onlineExtensions: onlineExtensionsFromComposable,
    extensions
} = useExtensions()

const { data: loadedData } = useWidgetDataWithRefreshInterval<UsersData, {data: WidgetExtended}, 'data'>(props, 'data')

const cardSizes: ICardSize[] = [
    {
        name: 'extra-small',
        width: 292,
        columns: 6
    },
    {
        name: 'small',
        width: 354,
        columns: 5
    },
    {
        name: 'medium',
        width: 446,
        columns: 4
    },
    {
        name: 'large',
        width: 600,
        columns: 3
    },
]

const localWidgetData = ref(cloneDeep(props.data))

/* Computed */
// Get sort options with translated labels at runtime
const extensionsSortOptions = computed(() => getExtensionsSortOptions())

const sortByModel = computed<string>({
    get () {
        const params = localWidgetData.value.WidgetLayout?.sortingParams
        if (!params?.prop && !params?.order) return 'number-asc'

        return extensionsSortOptions.value.find(opt =>
            opt.order === params.order && opt.prop === params.prop
        )?.value ?? 'number-asc'
    },
    set (val) {
        const option = extensionsSortOptions.value.find(o => o.value === val)
        if (!option || !localWidgetData.value.WidgetLayout) return

        const current = localWidgetData.value.WidgetLayout.sortingParams
        if (current?.prop === option.prop && current?.order === option.order) return

        localWidgetData.value.WidgetLayout.sortingParams = {
            order: option.order,
            prop: option.prop
        }
        updateWidget()
    }
})

const sortBy = computed(() => {
    return extensionsSortOptions.value.find(data => data.value === sortByModel.value)
})
const settings = computed(() => {
    return props.data.WidgetLayout.settings || realTimeSettings
})
const layoutColumns = computed({
    get () {
        return get(localWidgetData.value.WidgetLayout, 'layoutColumns') || maxCardsToDisplay.value
    },
    set (val) {
        localWidgetData.value.WidgetLayout.layoutColumns = val
        updateWidget()
    }
})
const columnsMode = computed({
    get () {
        return get(localWidgetData.value, 'WidgetLayout.settings.columnsMode', realTimeSettings.columnsMode)
    },
    set (val) {
        if (!localWidgetData.value.WidgetLayout.settings) {
            localWidgetData.value.WidgetLayout.settings = {}
        }
        localWidgetData.value.WidgetLayout.settings.columnsMode = val
        updateWidget()
    }
})
const layoutColumnsBasedOnColumnsMode = computed(() => {
    if (isStaticColumnsMode.value) {
        return layoutColumns.value
    } else {
        switch (columnsSize.value) {
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.AUTO:
                return bestCardSize.value.columns
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.EXTRA_SMALL:
                return 6
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.SMALL:
                return 5
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.MEDIUM:
                return 4
            case REAL_TIME_WIDGET_CARDS_COLUMN_SIZE.LARGE:
                return 3
            default:
                return 1
        }
    }
})
const columnSizeList = computed(() => {
    return Object.values(REAL_TIME_WIDGET_CARDS_COLUMN_SIZE).map(el => {
        return {
            label: i18n.t(`settings.extension.autoColumns.size.${el}`),
            value: el
        }
    })
})
const columnsSize = computed({
    get () {
        return get(localWidgetData.value, 'WidgetLayout.settings.columnsSize', realTimeSettings.columnsSize)
    },
    set (val) {
        if (!localWidgetData.value.WidgetLayout.settings) {
            localWidgetData.value.WidgetLayout.settings = {}
        }
        localWidgetData.value.WidgetLayout.settings.columnsSize = val
        updateWidget()
    }
})
const isStaticColumnsMode = computed(() => {
    return columnsMode.value === REAL_TIME_WIDGET_CARDS_COLUMN_MODE.STATIC
})
const widgetSettings = computed(() => {
    return props.data.WidgetLayout?.settings || realTimeSettings
})
const renderColumns = computed(() => {
    if (!isStaticColumnsMode.value) {
        return {}
    }

    let columns = ''

    const columnsCount = sortedExtensionsDebounced.value.length < layoutColumns.value
        ? sortedExtensionsDebounced.value.length
        : layoutColumns.value

    times(columnsCount, () => {
        columns += ' auto'
    })
    return {
        'grid-template-columns': columns,
    }
})
const maxCardsToDisplay = computed(() => {
    return Math.floor(pageWidth.value / cardWidth - 1)
})

const usersData = computed(() => {
    if (loadedData.value?.length) {
        return loadedData.value.reduce<Record<string| number, UserData>>((accum, user) => {
            accum[user.exten_id] = user
            return accum
        }, {} )
    }
    return null
})

const showLoggedOutUsers = computed(() =>
    props.data.WidgetLayout?.settings?.showLoggedOutUsers
)
const { expiredKeys, markExpired, clearExpired } = useExpiredRecentlyLoggedOutKeys(() => settings.value)
const onlineExtensions = computed(() => {
    const sourceExtensions = showLoggedOutUsers.value
        ? extensions.value
        : onlineExtensionsFromComposable.value

    return sourceExtensions.filter((ext) => {
        if (!currentAccountEntitiesExtensions.value[ext.number]) return false

        if (shouldSkipExpiredLogout(ext, settings.value)) return false

        return true
    })
})
const { filteredItems: filteredExtensions } = useEntityPositiveNegativeFilters(localWidgetData, EXTENSION_LIST_PARAMETER_NAME, onlineExtensions, extensionIdProp)

const mergedFilteredExtensions = computed(() => {
    if (usersData.value) {
        return filteredExtensions.value.map((item) => {
            const user = usersData.value ? usersData.value[item.number] : null
            if (user) {
                return {
                    ...item,
                    allDataSummary: user
                }
            }
            return item
        })
    }
    return filteredExtensions.value
})

const sortedExtensions = computed(() => {
    const updatedExtensions = mergedFilteredExtensions.value.map(ext => {
        const extension: SortedExtension = {
            ...ext,
            statusTime: getInitialExtensionTime(ext, settings.value)
        }
        if (extension.calls && extension.calls.length) {
            const earliestCall = extension.calls.reduce((a, b) =>
                a.callStarted_UTC_CLIENT < b.callStarted_UTC_CLIENT ? a : b
            )
            extension.callDuration = getInitialTime(earliestCall.callStarted_UTC_CLIENT)
        }

        return extension
    })

    if (!sortBy.value) {
        return updatedExtensions
    }

    if (sortBy.value.prop === 'callDuration') {
        const [ withoutCalls, withCalls ] = partition(updatedExtensions, ext => !ext.calls.length)

        const sortByCallStarted = (a: SortedExtension, b:SortedExtension) => {
            const aVal = getEarliestCallTime(a)
            const bVal = getEarliestCallTime(b)
            return compareValues(aVal, bVal, sortBy.value?.order)
        }

        withCalls.sort(sortByCallStarted)

        const sortedWithoutCalls = orderBy(withoutCalls, 'lastAnsweredCallEventEpoch', 'asc')

        return sortBy.value.order === 'asc'
            ? withCalls.concat(sortedWithoutCalls)
            : sortedWithoutCalls.concat(withCalls)
    }
    const [ unSortable, sortable ] = partition(updatedExtensions, ext =>
        get(ext, sortBy.value.prop) === undefined || ext.representativeStatus === Statuses.LOGOUT
    )

    const sorted = orderBy(sortable, sortBy.value.prop, sortBy.value.order)
    return sorted.concat(unSortable)
})
const sortedExtensionsVisible = computed(() => {
    if (!isRecentlyLoggedOutEnabled(settings.value)) return sortedExtensions.value
    return sortedExtensions.value.filter((ext) =>
        keepAfterExpiredKeys(
            `${ext.userID}:${ext.number}`,
            ext.representativeStatus,
            expiredKeys.value,
            clearExpired
        )
    )
})
const sortedExtensionsDebounced = useThrottle(sortedExtensionsVisible, 500)

const cardWrapStyles = computed(() => {
    const styles: Record<string, string> = {
        maxHeight: `${BASE_CARD_HEIGHT}px`
    }
    const currentColumns = layoutColumnsBasedOnColumnsMode.value

    const matchingSize = cardSizes.find(size => size.columns === currentColumns)

    if (matchingSize) {
        styles.width = `${matchingSize.width}px`
    } else if (!currentColumns || currentColumns < 4) {
        styles.width = `${cardSizes.find(size => size.name === 'large')?.width || 600}px`
    } else if (currentColumns >= 6) {
        styles.width = `${cardSizes.find(size => size.name === 'extra-small')?.width || 292}px`
    } else {
        styles.width = `${446 - (currentColumns - 4) * 92}px`
    }
    return styles
})

const bestCardSize = computed(() => {
    let bestSize: ICardSize | null = null

    if (parent.value) {
        const computedStyles = window.getComputedStyle(parent.value)
        const gap = parseInt((computedStyles.gap || computedStyles.columnGap))
        for (const size of cardSizes) {
            const cardsPerRowWithoutGap = Math.floor((widgetWidth.value) / (size.width))
            const cardsPerRow = Math.floor((widgetWidth.value) / (size.width + (cardsPerRowWithoutGap - 1) * gap))

            const rowsAvailableWithoutGap = Math.floor((widgetHeight.value) / BASE_CARD_HEIGHT)
            const rowsAvailable = Math.floor((widgetHeight.value) / (BASE_CARD_HEIGHT + (rowsAvailableWithoutGap - 1) * gap))

            const cardsThatFit = cardsPerRow * rowsAvailable

            if (
                cardsThatFit >= sortedExtensionsDebounced.value.length &&
                (!bestSize || size.width  > bestSize.width)
            ) {
                bestSize = size
            } else if (
                cardsThatFit > 0 &&
                (!bestSize || cardsThatFit > Math.floor((widgetWidth.value) / bestSize.width) * Math.floor((widgetHeight.value) / BASE_CARD_HEIGHT))
            ) {
                bestSize = size
            }
        }
    }

    return bestSize ?? cardSizes[0]
})

/* Methods */
function updateWidget () {
    emit('on-update', localWidgetData.value)
}
function compareValues (a: string | number, b: string | number, order: string) {
    return order === 'asc'
        ? (a > b
            ? 1
            : a < b
                ? -1
                : 0)
        : (a < b
            ? 1
            : a > b
                ? -1
                : 0)
}
function getEarliestCallTime (extension: Extension) {
    if (!extension.calls.length) {
        return null
    }

    if (extension.calls.length === 1) {
        return extension.calls[0].callStarted_UTC_CLIENT
    }

    return extension.calls.reduce(
        (acc, call) => {
            return acc < call.callStarted_UTC_CLIENT
                ? acc
                : call.callStarted_UTC_CLIENT
        }, extension.calls[0].callStarted_UTC_CLIENT
    )
}
const updateGrid = (val: number) => {
    layoutColumns.value = val
}

/*watch*/
watch(
    () => props.data,
    (newV) => {
        localWidgetData.value = cloneDeep(newV)
    },
    {
        deep: true
    }
)
</script>


<style scoped lang="scss">
.square {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 2px solid var(--silver-color);

    &:hover {
        background: var(--silver-color);
        border: 2px solid var(--primary-color);
    }
}

.mx-margin--1 {
    margin-right: 1px;
    margin-left: 1px;
}

.redesign-extension-cards-wrap {
    height: fit-content;

    &.columns-auto {
        @apply flex justify-center;

        .redesign-extension-cards {
            @apply inline-flex flex-wrap justify-center;
        }
    }

    .redesign-extension-cards {
        @apply grid w-max mx-auto;
    }
}

.redesign-columns-management {
    @apply rounded mt-1 flex flex-col p-2;

    .redesign-columns-management-field-label-wrapper {
        @apply rounded-t border-b-2;

        .redesign-columns-management-field-label {
            @apply px-2 text-main-sm font-medium;
        }
    }
}
</style>
